import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ContentHeader from 'components/ContentHeader';
import RewardMenu from 'components/RewardMenu';

import APIHandler from 'config/APIHandler';
// eslint-disable-next-line import/order
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';
import { legacyRemCss } from 'helpers/legacyRem';

import BookingState from '../BookingsView/BookingState';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18n from 'i18n-react';
import DataTable, { type TableColumn } from 'react-data-table-component';

interface Booking {
  booking_number?: string;
  reservation_number?: string;
  status?: string;
  departure_date?: string;
  agent_last_name?: string;
  number_of_guests?: number;
  length_of_stay?: number;

  [key: string]: unknown;
}

interface ShowState {
  showKey?: string;
  data?: Booking[];

  [key: string]: unknown;
}

type HideBreakpoint = 'sm' | 'md' | 'lg' | 'xl';

interface DashboardFilterSchema {
  key: string;
  icon?: 'check' | 'clock' | 'ban' | 'th-list';
  i18nKey?: string;
  statuses: string[];
}

interface DashboardColumnSchema {
  name: string;
  selector: string; // key in booking object
  sortable?: boolean;
  hide?: HideBreakpoint;
  format?: 'date' | 'text' | 'number';
}

interface DashboardTableSchema {
  filters: DashboardFilterSchema[];
  columns: DashboardColumnSchema[];
}

async function loadDashboardTableSchema(appId: string): Promise<DashboardTableSchema | null> {
  try {
    const url = `https://front.travpromobile.com/${appId}/dashboard.schema.json`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;

    const json = (await res.json()) as DashboardTableSchema;

    if (!json || !Array.isArray(json.columns) || !Array.isArray(json.filters)) return null;
    return json;
  } catch {
    return null;
  }
}

function buildColumnsFromSchema(schema: DashboardTableSchema): TableColumn<Booking>[] {
  return schema.columns.map((col) => ({
    name: col.i18nKey ? i18n.translate(col.i18nKey) : col.name,
    sortable: Boolean(col.sortable),
    // Remove 'hide' if not compatible with DataTable
    selector: (row: Booking) => {
      const raw = row[col.selector as keyof Booking];
      if (col.format === 'date') {
        if (!raw) return '';
        const d = new Date(String(raw));
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
      }
      return raw == null ? '' : String(raw);
    },
  }));
}

function applyStatusesFilter(bookings: Booking[], statuses: string[]): Booking[] {
  if (statuses.includes('*')) return bookings;
  return bookings.filter((b) => statuses.includes(String(b.status ?? '')));
}

const iconMap: Record<NonNullable<DashboardFilterSchema['icon']>, [string, string]> = {
  check: ['fas', 'check'],
  clock: ['fas', 'clock'],
  ban: ['fas', 'ban'],
  'th-list': ['fas', 'th-list'],
};

const DashboardView: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  // const [contests, setContests] = useState<Contest[]>([]);
  const [show, setShowState] = useState<ShowState>({});
  const [activeBtn, setActiveBtn] = useState<string>('View All');

  // NEW: schema state
  const [tableSchema, setTableSchema] = useState<DashboardTableSchema | null>(null);

  const getBookingsData = useCallback(async () => {
    try {
      setLoading(true);

      const token = await StorageHandler.load(storageKeys.accessToken);
      const result = await APIHandler.getRewards(import.meta.env.VITE_APP_ID, token);

      const { response } = result || {};
      const fetchedBookings: Booking[] = Array.isArray(response) ? response : [];

      if (Array.isArray(fetchedBookings) && fetchedBookings.length > 0) {
        setBookings(fetchedBookings);

        setShowState({
          showKey: 'View All',
          'View All': true,
          data: [...fetchedBookings],
        });
      } else {
        setBookings([]);
      }
    } catch (error) {
      setBookings([]);
      setShowState({});
    } finally {
      setLoading(false);
    }
  }, []);

  const getIncentivesData = useCallback(async () => {
    try {
      const lang = (await StorageHandler.load(storageKeys.lang)) as { code?: string } | null;

      const result = await APIHandler.getContests(import.meta.env.VITE_APP_ID, {
        currentDate: new Date().toISOString(),
        marketFilter: [{ id: String(lang?.code || '').toUpperCase() }],
      });

      if (Array.isArray(result) && result.length > 0) {
        setContests(result);
      } else {
        setContests([]);
      }
    } catch (error) {
      setContests([]);
    }
  }, []);

  const getTableSchema = useCallback(async () => {
    const appId = String(import.meta.env.VITE_APP_ID);
    const schema = await loadDashboardTableSchema(appId);
    setTableSchema(schema);
  }, []);

  useEffect(() => {
    void getTableSchema();
    void getBookingsData();
    void getIncentivesData();
  }, [getBookingsData, getIncentivesData, getTableSchema]);

  const setShow = useCallback((key: string, booking: Booking[]) => {
    setActiveBtn(key);

    setShowState((prevShow) => {
      const updatedShow = Object.keys(prevShow).reduce<Record<string, unknown>>((acc, item) => {
        acc[item] = item === key;
        return acc;
      }, {});

      if (booking.length >= 0) {
        updatedShow.showKey = key;
        updatedShow.data = [...booking];
      }

      return updatedShow;
    });
  }, []);

  /**
   * Columns: schema-driven if schema exists, otherwise fallback to the original hardcoded columns
   */
  const columns: TableColumn<Booking>[] = useMemo(() => {
    if (tableSchema) return buildColumnsFromSchema(tableSchema);

    // fallback columns
    return [
      {
        name: 'Confirm Number',
        selector: (row) => String(row.booking_number ?? ''),
        sortable: true,
      },
      {
        name: 'Flight Number',
        selector: (row) => String(row.reservation_number ?? ''),
        sortable: true,
      },
      {
        name: 'Status',
        selector: (row) => String(row.status ?? ''),
        sortable: true,
      },
      {
        name: 'Depature Date',
        selector: (row) => String(row.departure_date ?? ''),
        sortable: true,
      },
      {
        name: 'Last Name',
        selector: (row) => String(row.agent_last_name ?? ''),
        sortable: true,
      },
      {
        name: 'Total Travelers',
        selector: (row) => String(row.number_of_guests ?? ''),
        sortable: true,
      },
      {
        name: 'Total Nights',
        selector: (row) => String(row.length_of_stay ?? ''),
        sortable: true,
      },
    ];
  }, [tableSchema]);

  const mobile = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get('mobile'));
  }, []);

  const sortedFilters = useMemo(() => {
    if (!tableSchema?.filters) return [];
    const order = ['Approved Entries', 'Pending Entries', 'Declined Entries', 'View All'];
    return [...tableSchema.filters].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }, [tableSchema]);

  return (
    <div className="contentContainer dashboardView">
      <RewardMenu />

      <section className="headerFirst">
        <ContentHeader
          header={i18n.translate('rewards.dashboardView.title')}
          subtitle={i18n.translate('rewards.dashboardView.subtitle')}
        />
      </section>

      <hr />

      <div className={`DashboardView ${loading ? 'overflow-hidden' : ''}`}>
        <div className="dashboardHolder">
          <div className="progress">
            {!loading && (
              <>
                <div className="tableNav">
                  {/* NEW: schema-driven filters when schema exists */}
                  {tableSchema &&
                    sortedFilters.map((f) => {
                      const filtered = applyStatusesFilter(bookings, f.statuses);

                      return (
                        <div key={f.key} onClick={() => setShow(f.key, filtered)}>
                          <button className={activeBtn === f.key ? 'btn active' : 'btn'}>
                            {f.icon && <FontAwesomeIcon icon={iconMap[f.icon] as any} />}

                            <span>{filtered.length}</span>

                            <span
                              className="head"
                              dangerouslySetInnerHTML={{
                                __html: String((f.i18nKey ? i18n.translate(f.i18nKey) : f.key) ?? '').replace(
                                  ' ',
                                  '<br/>',
                                ),
                              }}
                            />
                          </button>
                        </div>
                      );
                    })}

                  {!tableSchema &&
                    ['Approved Entries', 'Pending Entries', 'Declined Entries', 'View All'].map((key) => {
                      const booking = bookings.filter(({ status }) => {
                        const state = (BookingState as Record<string, { status: string | string[] }>)[key];
                        if (Array.isArray(state.status)) {
                          return state.status.includes(status!);
                        }
                        return state.status === status;
                      });

                      return (
                        <div key={key} onClick={() => setShow(key, booking)}>
                          <button className={activeBtn === key ? 'btn active' : 'btn'}>
                            {key === 'Approved Entries' && <FontAwesomeIcon icon={['fas', 'check'] as any} />}
                            {key === 'Pending Entries' && <FontAwesomeIcon icon={['fas', 'clock'] as any} />}
                            {key === 'Declined Entries' && <FontAwesomeIcon icon={['fas', 'ban'] as any} />}
                            {key === 'View All' && <FontAwesomeIcon icon={['fas', 'th-list'] as any} />}

                            <span>{booking.length}</span>

                            <span
                              className="head"
                              dangerouslySetInnerHTML={{
                                __html: String(
                                  i18n.translate(
                                    `rewards.dashboardView.state.${key
                                      .split(' ')
                                      .join('')
                                      .replace(/^./, (str) => str.toLowerCase())}`,
                                  ) ?? '',
                                ).replace(' ', '<br/>'),
                              }}
                            />
                          </button>
                        </div>
                      );
                    })}
                </div>

                <ContentHeader
                  header={i18n.translate('rewards.dashboardView.manage')}
                  subtitle={i18n.translate('rewards.dashboardView.manageSub')}
                />

                <div className="table-booking">
                  <DataTable
                    className="bookings-table"
                    columns={columns}
                    data={Array.isArray(show?.data) ? show.data : []}
                    defaultSortFieldId={1}
                    noDataComponent={
                      <div>
                        <br />
                        {i18n.translate('rewards.dashboardView.noBookings')}
                        <br />
                        <br />
                      </div>
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {mobile && (
        <style>
          {`.contentContainer { margin-top: ${legacyRemCss(5)}; } .header { display: none; } .cookieConsent { display: none; }`}
        </style>
      )}
    </div>
  );
};

export default DashboardView;
